import { useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { ArrowLeft, Camera, Images, Loader2, PenLine } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { captureLoad, describeError, useBillingStatus, useCaptureLoad } from "@/lib/api";
import { PaywallCard } from "@/components/billing";
import {
  CAPTURE_CONCURRENCY,
  mapWithConcurrency,
  type BatchCaptureResult,
} from "@/lib/batch";
import { BatchReview } from "./batch-review";

// Fake-progress labels for the synchronous ~5-25s capture pipeline,
// matching the real stages the server runs (scan → OCR → eirParser).
// Single-upload only — a batch has a real count to show instead.
const STAGE_LABELS = [
  "Uploading…",
  "Correcting perspective…",
  "Reading the ticket…",
  "Pulling out the details…",
];

export function Capture() {
  const [, navigate] = useLocation();
  // Capture is the gated action, so this screen owns the paywall: the
  // server would answer 402, and showing that as a failed upload after the
  // driver picked eight photos would be a cruel way to say "subscribe".
  const billing = useBillingStatus();
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const bulkInputRef = useRef<HTMLInputElement>(null);
  const [stageIdx, setStageIdx] = useState(0);
  const capture = useCaptureLoad();

  // Bulk state. `done` counts finished tickets (succeeded or failed) so
  // the driver sees "Reading 3 of 8…" advance rather than a spinner that
  // could be stuck. `results` non-null means the batch is over and the
  // review screen owns the page.
  const [total, setTotal] = useState(0);
  const [done, setDone] = useState(0);
  const [results, setResults] = useState<BatchCaptureResult[] | null>(null);

  const bulkRunning = total > 0 && results === null;

  const handleFile = async (file: File) => {
    setStageIdx(0);
    // Cycle stage labels every 3s while the synchronous upload is in
    // flight. Fake progress, but matches the real pipeline timing.
    const interval = window.setInterval(() => {
      setStageIdx((i) => Math.min(i + 1, STAGE_LABELS.length - 1));
    }, 3000);

    try {
      const res = await capture.mutateAsync(file);
      window.clearInterval(interval);
      // Straight to review: a one-off gets the full pricing screen, where
      // an amount the parser read is prefilled and still confirmable.
      navigate(`/l/${res.load.id}/review`);
    } catch (err) {
      window.clearInterval(interval);
      toast.error(err instanceof Error ? err.message : "Capture failed");
    }
  };

  // The default path: N tickets in one go. Bounded concurrency because
  // OCR is synchronous per request; a per-ticket failure is recorded as a
  // failed ROW and never rejects the batch.
  const handleFiles = async (files: File[]) => {
    setTotal(files.length);
    setDone(0);
    setResults(null);

    const captured = await mapWithConcurrency(
      files,
      CAPTURE_CONCURRENCY,
      async (file): Promise<BatchCaptureResult> => {
        try {
          const res = await captureLoad(file);
          return { fileName: file.name, load: res.load, error: null };
        } catch (err) {
          return { fileName: file.name, load: null, error: describeError(err) };
        } finally {
          setDone((d) => d + 1);
        }
      },
    );

    setResults(captured);
  };

  const onPickOne = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
  };

  const onPickMany = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    void handleFiles(files);
  };

  const isUploading = capture.isPending || bulkRunning;

  return (
    <div className="min-h-dvh max-w-2xl mx-auto px-4 py-6 space-y-6">
      <header className="flex items-center gap-3">
        <Link href="/">
          <button type="button" aria-label="Back" className="p-2 -ml-2 text-muted-foreground">
            <ArrowLeft className="h-5 w-5" />
          </button>
        </Link>
        <h1 className="text-2xl font-bold tracking-tight">
          {results ? "Review loads" : "Capture loads"}
        </h1>
      </header>

      {billing.data?.allowed === false ? (
        <PaywallCard status={billing.data} />
      ) : results ? (
        <BatchReview
          results={results}
          onDone={() => {
            toast.success("Loads saved");
            navigate("/");
          }}
        />
      ) : isUploading ? (
        <Card className="p-8 flex flex-col items-center gap-4 text-center">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
          <div className="text-sm text-muted-foreground">
            {bulkRunning ? `Reading ${Math.min(done + 1, total)} of ${total}…` : STAGE_LABELS[stageIdx]}
          </div>
          <p className="text-xs text-muted-foreground/70">
            This takes a few seconds per ticket. Keep this screen open.
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {/* The one thing that has to happen BEFORE the photo — no
              amount on the ticket means no amount to read, and the driver
              is back to typing every one of them. */}
          <Card className="p-4 flex gap-3 border-amber-400/70 bg-amber-50/60 dark:bg-amber-950/20">
            <PenLine className="h-5 w-5 text-amber-600 dark:text-amber-500 shrink-0" />
            <p className="text-sm">
              Write each load&apos;s pay clearly as{" "}
              <span className="font-mono font-semibold">$####</span> on the ticket before you
              photograph it.
            </p>
          </Card>

          {/* Bulk is the default: pick the whole day's tickets at once. */}
          <input
            ref={bulkInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            data-testid="gallery-input"
            onChange={onPickMany}
          />
          {/* One-off / re-shoot: rear camera on mobile. */}
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            data-testid="camera-input"
            onChange={onPickOne}
          />

          <Button
            size="lg"
            className="w-full h-16 text-base gap-3"
            onClick={() => bulkInputRef.current?.click()}
          >
            <Images className="h-6 w-6" />
            Select tickets
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="w-full h-16 text-base gap-3"
            onClick={() => cameraInputRef.current?.click()}
          >
            <Camera className="h-6 w-6" />
            Take photo
          </Button>
          <p className="text-xs text-muted-foreground text-center pt-4">
            Photograph the interchange tickets. JPG, PNG, or HEIC, up to 50 MB each.
          </p>
        </div>
      )}
    </div>
  );
}
